import React, { useEffect, useRef, useState } from "react";
import store from "../../../store"
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/es/form/Form";
import { Form, Typography, Flex, Grid, Button, Card, App } from "antd";
import ProductReplaceFooter from "./ProductReplaceFooter";
import SearchAutoComplete from "../../../components/SearchAutoComplete";
import StaticAutoComplete from "../../../components/StaticAutoComplete";
import InputField from "../../../components/Input/Input";
import ProductReplaceTable from "./ProductReplaceTable";
import ProductReturnTable from "./ProductReturnTable";
import {
  fetchGivenItemsList,
  fetchCustomerSpecialPrice,
  fetchCustomersList,
  fetchFilteredVouchers,
  fetchFilteredItems,
  fetchSalesVoucherDetails,
  fetchItemBySearch,
  fetchReplaceVoucherNumber,
} from "../productReplaceThunk";
import {
  setFormField,
  emptyGivenRow,
  setResetProductReplace,
  calculateFooterTotals,
} from "../productReplaceSlice";
import { getAccessType } from "../../../lib/functions";
import {
  saveDraft,
  clearDraft,
} from "../../../store/commonSlices/uiDraftsSlice";
import { selectDraft } from "../../../store/commonSelectors/uiDraftsSelectors";


const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreateProductReplaceForm = ({
  form,
  authInfo,
  onSaveReplace,
  voucherNumber,
  setSelectedCustomer,
  selectCustomerInfo,
  replaceAccs = [],
  givenItemsList = [],
  returnItemsList = [],
  footerValues = {},
  vouchersList = [],
  replace_id
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const { message, modal } = App.useApp();

  const customer = selectCustomerInfo;

  const {selectedVoucherDetails} = useSelector(s => s.productReplace)

  // Track previous state to handle resets
  const prevHasCustomer = useRef(!!customer?.acc_id);
  const prevHasInvoice = useRef(!!form.getFieldValue("sale_invoice"));

  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("employee_entry");
    access_type_set(access);
  }, []);


  // Fetch voucher number
  useEffect(() => {
    if(!replace_id){
    dispatch(fetchReplaceVoucherNumber());
    }
  }, [dispatch, replace_id]);

  // Refetch voucher when return list updates
  useEffect(() => {
    if (!replace_id && customer?.acc_id && returnItemsList.length) {
      dispatch(fetchReplaceVoucherNumber());
    }
  }, [customer, returnItemsList, dispatch]);

  // Reset when customer or invoice removed
  useEffect(() => {
    const hasCustomer = !!customer?.acc_id;
    const hasInvoice = !!form.getFieldValue("sale_invoice");
    const lostCustomer = prevHasCustomer.current && !hasCustomer;
    const lostInvoice = prevHasInvoice.current && !hasInvoice;

    if (lostCustomer || lostInvoice) {
      dispatch(setResetProductReplace());
      form.resetFields();
    }

    prevHasCustomer.current = hasCustomer;
    prevHasInvoice.current = hasInvoice;
  }, [customer, form, dispatch]);

  


  // Recalculate totals
  useEffect(() => {
    dispatch(calculateFooterTotals());
  }, [givenItemsList, returnItemsList, footerValues.serviceCharge, footerValues.paidAmount, footerValues.payServiceCart]);

  // Handlers
  const handleSaleInvoiceSelect = (invoice) => {
  
    if (!invoice) return;
    dispatch(setFormField({ field: "selectedVoucherDetails", value: invoice }));    
    if (invoice.sale_id) {
      dispatch(fetchFilteredItems(invoice.sale_id));
      dispatch(fetchSalesVoucherDetails(invoice.sale_id));
    }
  };

  const handleCustomerSelect = (customerData) => {
    if (customerData === "add_new") {
    
    // SAVE DRAFT HERE
    dispatch(saveDraft({
      key: "product_replace",
      data: {
        form: form.getFieldsValue(),
        redux: store.getState().productReplace,
      }
    }));

    navigate(`/customers/addNewCustomer?returnUrl=${location.pathname}`);
    return;
  }
    dispatch(setSelectedCustomer(customerData));
    if(!replace_id){
    dispatch(fetchFilteredVouchers())
      .unwrap()
      .then((vouchers) => {
        if (vouchers.length === 1) {
          dispatch(fetchFilteredItems(vouchers[0].sale_id));
        }
      });
    }
  };

  // add new item 
  const onAddNewItem = () => {
  dispatch(saveDraft({
    key: "product_replace",
    data: {
      form: form.getFieldsValue(),
      redux: store.getState().productReplace
    }
  }));
  navigate(`/inventory/product/addNewItem?returnUrl=${location.pathname}`);
}


  const onChangeGiven = (valueOrObj, key, field) => {
    dispatch(setFormField({
      field: "givenItemsList",
      value: givenItemsList.map((it) => {
        if (it.key !== key) return it;
        if (typeof valueOrObj === "object" && !field) return { ...it, ...valueOrObj };
        return { ...it, [field]: valueOrObj };
      }),
    }));
  };

  const onChangeReturn = (valueOrObj, key, field) => {
    dispatch(setFormField({
      field: "returnItemsList",
      value: returnItemsList.map((it) => {
        if (it.key !== key) return it;
        if (typeof valueOrObj === "object" && !field) return { ...it, ...valueOrObj };
        return { ...it, [field]: valueOrObj };
      }),
    }));
  };

  const handleFooterChange = (value, field) => {
    dispatch(setFormField({
      field: "footerValues",
      value: { ...footerValues, [field]: value },
    }));
    dispatch(calculateFooterTotals()); // ✅ force recalc
  };

  
const handleSubmit = () => {
  if (!customer?.acc_id) {
    return message.warning("Select customer!");
  }

  if (!givenItemsList.length || !givenItemsList[0].product) {
    return message.warning("Please add at least one product!");
  }

  // qty > 0 for all given
  const badGiven = givenItemsList.find((g) => Number(g.quantity || 0) <= 0);
  if (badGiven) return message.warning("Please enter valid quantity for given items!");

  // serial rules (basic)
  const serialMismatch = givenItemsList.some((g) => {
    const isSerial = (g.fullItem?.is_serial ?? g.is_serial) === "yes";
    if (!isSerial) return false;
    const q = Number(g.quantity || 0);
    const serialsCount = (g.fullItem?.serials || g.serials || []).length;
    // If you later add UI to select serials, enforce equality:
    // return q !== serialsCount;
    return q <= 0; // for now: serial item must have qty > 0
  });
  if (serialMismatch) {
    return message.warning("Serial items require a valid quantity.");
  }

  onSaveReplace(customer, givenItemsList, footerValues);
};


  useEffect(() => {
    dispatch(fetchCustomersList(""));
  }, [dispatch]);

  useEffect(() => {
  if (customer?.acc_name) {
    form.setFieldsValue({
      search_customer: customer.acc_name,
      sale_invoice: selectedVoucherDetails?.voucher_no || "",
    });
  }
}, [customer, selectedVoucherDetails]);

  // ===== UI =====
  return (
    <div className="product-replace-form">
      <Title level={3}>Product Replace</Title>

      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        {/* Buttons */}
        {!screens.xs && <Flex gap={16} style={{ marginBottom: "24px" }}>
          <Button
            size="large"
            className="traic-button"
            onClick={() => navigate("/sales-dashboard")}
          >
            Back
          </Button>
          {access_type == "WRITE" && <>
            <Button
              type="primary"
              size="large"
              className="traic-button"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </>}
        </Flex>}

        {/* Two-column layout */}
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          {/* Left column */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              label="Search Customer"
              name="search_customer"
              required
              rules={[{ required: true }]}
            >
              <SearchAutoComplete
                placeholder="Customer"
                searchThunk={(text) => dispatch(fetchCustomersList(text))}
                resultSelector={(s) => s.productReplace.customersList}
                value={customer || { display_text: "" }}
                onSelect={handleCustomerSelect}
                showAddNew={false}
              />
            </Form.Item>

            <Form.Item label="Institution Name">
              <InputField value={customer?.institution_name} disabled />
            </Form.Item>

            <Form.Item label="Customer Address">
              <InputField value={customer?.address} disabled />
            </Form.Item>
          </Flex>

          {/* Right column */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Select Invoice" //name="sale_invoice" 
            rules={[{ required: true }]}>
              <StaticAutoComplete
                optionsList={vouchersList}
                onSelect={handleSaleInvoiceSelect}
                value={selectedVoucherDetails?.display_text || ""}                  
                placeholder="Select Invoice"
                disabled={!vouchersList?.length}
              />
            </Form.Item>

            <Form.Item label="Contact No">
              <InputField value={customer?.contact_no} disabled />
            </Form.Item>

            <Form.Item label="Voucher No">
              <InputField value={voucherNumber || ""} disabled />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>

      {/* Returned Items */}
      <Card
        style={{ marginTop: 24, marginBottom: 32, maxWidth: "1300px"  }}
        bordered
        title={<Title level={4} style={{ margin: 0 }}>Returned Items (From Sale Invoice)</Title>}
      >
        <ProductReturnTable
          type="return"
          itemsList={returnItemsList}
          handleInputChange={onChangeReturn}
          footerValues={footerValues}
          handleFooterChange={handleFooterChange}
          authInfo={authInfo}
        />
      </Card>

      {/* Given Items */}
      <Card
        style={{ marginBottom: 32, maxWidth: "1300px" }}
        bordered
        title={<Title level={4} style={{ margin: 0 }}>Given Items (Newly Provided)</Title>}
      >
        <ProductReplaceTable
          type="given"
          itemsList={givenItemsList}
          handleInputChange={onChangeGiven}
          handleDeleteRow={(key) =>
            dispatch(setFormField({
              field: "givenItemsList",
              value: givenItemsList.filter((item) => item.key !== key),
            }))
          }
          handleAddRow={() =>
            dispatch(setFormField({
              field: "givenItemsList",
              value: [
                ...givenItemsList,
                emptyGivenRow(givenItemsList.length + 1),
              ],
            }))
          }
          authInfo={authInfo}
          footerValues={footerValues}
          handleFooterChange={handleFooterChange}
          selectedCustomer={customer}
          onAddNewItem={onAddNewItem}
          searchItemsThunk={(text) => dispatch(fetchItemBySearch(text))}
          selectItemsList={(state) => state.productReplace.itemSearchList}
          fetchStockForItem={(payload) => dispatch(fetchGivenItemsList(payload))}
          fetchCustomerSpPrice={(payload) => dispatch(fetchCustomerSpecialPrice(payload))}
          givenItemsList={givenItemsList}
          returnItemsList={returnItemsList}
        />
      </Card>

      {/* Footer */}
      <div style={{ width: "100%", maxWidth: "1300px" }}>
      <ProductReplaceFooter
        givenItems={givenItemsList}
        returnItems={returnItemsList}
        footerValues={footerValues}
        onFooterChange={handleFooterChange}
        authInfo={authInfo}
      />
      </div>
      {/* Bottom Buttons */}
      <Flex gap={16} style={{ marginTop: 32, marginBottom: 32 }}>
        <Button
          size="large"
          className="traic-button"
          onClick={() => navigate("/sales-dashboard")}
        >
          Back
        </Button>
        {access_type == "WRITE" && <>
          <Button
            type="primary"
            size="large"
            className="traic-button"
            onClick={handleSubmit}
          >
            Save
          </Button>
        </>}
      </Flex>
    </div>
  );
};

export default CreateProductReplaceForm;
